import { EGG, NON_VEGETARIAN, VEGETARIAN } from "../../utils/constants/shared";

import store from "../../redux-store";
import { getEnv } from "../../effects-bridge";

/**
 * Uplifted pieces of `helper/catalogueHelpers` (apps/web + apps/mobile, ~1250 lines each).
 *
 * This folder MIRRORS the app file it came from and is seeded with only the symbols that
 * were actually needed. When the next symbol from `catalogueHelpers` is required, add it HERE
 * rather than making a new per-symbol module: one core folder per source file keeps the mapping
 * obvious and lets the app file's re-export list grow one line at a time.
 *
 * Here so far — grouped by the uplift that pulled each one in:
 *   `LoginSlice`     `arrayToMap` (both; divergent on empty input)
 *   `TableViewSlice` `handleSaleUserMap` (mobile), `getChannelData` (web)
 *   `catalogueSlice` `setPriorityForItemTags` (mobile)
 *   `ItemsSlice`     `itemsMidMapForBarcode` (both, DIVERGENT), `getItemAttributes` +
 *                    `populateOptionSetVariantWrapper` (web), `itemsMidMap` +
 *                    `getCategoryItemsMap` + `getCategoryAccordingToItem` +
 *                    `getItemDataForCatalog` (mobile)
 *
 *   `saga/itemStatus` `flatItemsFromCategories` + `isDisplayOutOfStockItem` (both, IDENTICAL)
 *
 * Still in the apps (move on demand): `getItemDataForCatalog` (web's own two-arg version — a
 * DIFFERENT function from mobile's, see index.native.ts); `parseCatalog`, `parseCatagories`,
 * `parseMenu`, `parseOptionSets` (mobile) — the remaining closure of `catalogueSlice` and
 * `CatalogMapSlice`.
 *
 * ✅ `isDisplayOutOfStockItem`'s "needs a design decision, not a copy" note is RETIRED. The
 * decision was made by the user on 2026-08-21: a store-coupled helper MAY read through
 * core's own store. See the ledger's "BOTH SESSION-23 BLOCKING DECISIONS ARE ANSWERED".
 */

/**
 * `arrayToMap`'s common half. The apps' copies are identical EXCEPT on empty input — web
 * returns `null`, mobile returns `{}` — so the guard lives in each platform file and only
 * the reduce is shared. That difference is load-bearing: ~100 call sites, many written
 * `arrayToMap(x, "mid") || {}` precisely because web can hand back `null`.
 */
/**
 * Filter option variants down to those valid for a channel + branch, falling back to the
 * `Default` option when nothing matches.
 *
 * ⚠️ **Byte-identical in both apps** — normalised for quote style and TS annotations and diffed
 * token-for-token before collapsing, not assumed. It lived in `index.native.ts` only because the
 * web side had never been uplifted; that was one of the 27 "one definition, always" breaches
 * found by `pnpm audit:uplift`.
 *
 * ⚠️ The `Default` entry is skipped during the scan and appended ONLY if nothing else matched,
 * so a caller can receive the default even when it is excluded by channel/branch. Preserved.
 */
export function getOptionVariantForChannel(
  data: any[] = [],
  channelDefId: any,
  branchId: any
): any[] {
  let defaultOptionIndex = data.findIndex((option: any) => option.uid === "Default"),
    result: any[] = [];
  for (let i = 0; i < data.length; i++) {
    if (defaultOptionIndex === i) continue;
    else {
      let branches = data?.[i]?.branchIds || [], // no branchIds means applicable for all branches
        channelDefIds = data?.[i]?.channelDefIds || []; // no defId means applicable for all channels
      if (
        (channelDefIds.length ? channelDefIds.includes(channelDefId) : true) &&
        (branches.length ? branches.includes(branchId) : true)
      )
        result.push(data[i]);
    }
  }
  if (result.length === 0 && defaultOptionIndex > -1) result.push(data[defaultOptionIndex]);
  return result;
}

/**
 * Rank an item's tags into a fixed 3-slot priority queue: non-vegetarian, egg, vegetarian.
 *
 * ⚠️ **Byte-identical in both apps**, verified the same way as `getOptionVariantForChannel`.
 *
 * ⚠️ Returns a 3-slot array pre-filled with `-1`, so a caller must test for `-1` rather than for
 * a short array. Any tag that is not one of the three diet names is ignored entirely. Preserved.
 *
 * The three diet constants come from `utils/constants/shared.ts` — they are byte-identical on
 * both platforms, so this names `shared` explicitly rather than the folder; there is no variant
 * to choose between.
 */
export function setPriorityForItemTags(itemTagsObj: any = {}) {
  let allTagIds = Object?.keys(itemTagsObj);
  let priorityQueue: any[] = new Array(3).fill(-1);
  allTagIds?.forEach((id) => {
    const tagName = itemTagsObj?.[id]?.name?.toLowerCase();
    switch (tagName) {
      case NON_VEGETARIAN:
        priorityQueue[0] = id;
        break;
      case EGG:
        priorityQueue[1] = id;
        break;
      case VEGETARIAN:
        priorityQueue[2] = id;
        break;
      default:
        break;
    }
  });
  return priorityQueue;
}

/**
 * Reduce the catalogue's channelvos to `{ name, channelDefId }` pairs.
 *
 * ⚠️ **Byte-identical in both apps** — normalised and diffed token-for-token before collapsing.
 * It had only ever been uplifted on the WEB side, which is why mobile's app file still declared
 * its own copy; one of the breaches `pnpm audit:uplift` found.
 *
 * ⚠️ `returnData` is initialised to `[]` and then immediately reassigned by the `.map`. Dead but
 * harmless, and the apps' own — preserved.
 */
export function getChannelData(catalogData: any): any {
  let channelData = catalogData?.channelvos || [],
    returnData: any[] = [];

  returnData = channelData.map(({ name, channelDefId }: any) => ({ name, channelDefId }));

  return returnData;
}

export function reduceToMap(array: any[], id: string): any {
  return array.reduce((map: any, obj: any) => {
    map[obj[id]] = obj;
    return map;
  }, {});
}

/** Kept identical to both apps' `!array || !array.length || array.length === 0`. */
export function isEmptyArray(array: any): boolean {
  return !array || !array.length || array.length === 0;
}

/**
 * Flatten a category tree to a bare list of `itemId`s, including one level of sub-categories.
 *
 * **Byte-identical in both apps** (web `catalogueHelpers.js:880`, mobile `:764`), so it lives
 * here rather than in either variant.
 *
 * ⚠️ Preserved verbatim, including two rough edges the callers depend on: `category.items?.map`
 * is optional-chained but `category.subCategories.forEach` is NOT, so a category with no
 * `subCategories` key throws; and a missing `items` makes `concat(undefined)` append a literal
 * `undefined` element rather than skipping it. Both are the apps' existing behaviour.
 */
export function flatItemsFromCategories(categories: any[]): any[] {
  let flatItemList: any[] = [];
  categories.forEach((category: any) => {
    flatItemList = flatItemList.concat(category.items?.map((item: any) => item.itemId));
    category.subCategories.forEach((subCat: any) => {
      flatItemList = flatItemList.concat(subCat.items?.map((item: any) => item.itemId));
    });
  });
  return flatItemList;
}

/**
 * Is the "show out of stock" business setting on?
 *
 * **Byte-identical in both apps** (web `:914`, mobile `:775`) — including reading the SAME
 * reducer key, `businessStore`, which is mounted under that name in both stores.
 *
 * 🔴 **This is the first helper uplifted under the 2026-08-21 store-access decision.** The app
 * copies read the module-level `store` singleton; here that becomes `getState()` from
 * core's own store, which is the same read every other state-reading core module makes.
 * The decision is explicitly a STOPGAP — when the STORE phase lands, this is on the revisit list.
 */
export function isDisplayOutOfStockItem(): any {
  let businessData = store.getState()?.businessStore?.businessData;
  return businessData?.displayItemOutOfStockStatus;
}

/**
 * Flatten a variant's `variantValues` into one space-separated label ("Large Red").
 *
 * SHARED as of the web-adoption pass (it was `index.desktop.ts`-only for want of a mobile
 * caller). ONE definition: release's web already routes it to `catalog/catalogModel`'s
 * `attributeSummary` via `catalog/posCard`, so this is the same function under the POS's name —
 * mobile's barcode keys match web's. Returns `""` for an empty or absent list, never `undefined`.
 */
export { attributeSummary as getItemAttributes } from "../../catalog/catalogModel";

/**
 * ── WEB-ADOPTION ASSESSMENT: catalogueHelpers ─────────────────────────────────────────────
 *
 * Classified function-for-function in the web-adoption pass. Unlike flow 1, **this flow runs
 * the other way**: mobile's variant is 655 lines to web's 483, it owns 7 functions web lacks,
 * and it carries a native-only `CatalogMapSlice`. The two platforms solved catalogue lookup
 * differently — mobile parses eagerly into maps, web derives lazily behind caches — so "web is
 * the source of truth" is far weaker here than it was for my-orders. Expect reconciliation,
 * not adoption.
 *
 * Adopted: `getItemAttributes` and `getAggregatorLogo` (both pure, below). The second came
 * up through the `MyOrderCard` dependency closure rather than through this assessment —
 * `commonComponents/MyOrderCard/shared.ts` `getLogoType` is its only core caller so far.
 *
 * NOT adopted, each for a stated reason:
 *
 * · `needsMostSoldItemsFetch` — BLOCKED ON A WHOLE FEATURE, not on this helper. It reads
 *   `catalogStore.mostSoldItems` / `.mostSoldLastFetchingTime`, which exist only in
 *   `catalogueSlice/index.desktop.ts`, and the endpoint behind them (`POS.getMostSoldItems`)
 *   is web-only. Sharing the helper alone would hand mobile a function that reads `undefined`.
 *   Adopt most-sold as a feature, or not at all.
 *
 * · `populateOptionSetVariantWrapper` — needs a DEPENDENCY CLOSURE first. Its helper
 *   `populateOptionSetVariant` is a file-local `const` in `index.desktop.ts`, and it calls
 *   `arrayToMap`, which is deliberately divergent (`null` on web, `{}` on mobile, ~100 call
 *   sites written `|| {}` because of it). Mobile also has its own `parseOptionSets` covering
 *   overlapping ground, so this is a reconciliation of two designs, not a move.
 *
 * · The 8 divergent pairs (`arrayToMap`, `fetchCatalogCompleteData`, `getItemDataForCatalog`,
 *   `getItemPrice`, `isItemValidForChannel`, `itemsMidMapForBarcode`, `parseCatagories`,
 *   `parseMenu`) — ZERO have identical bodies. Same conclusion as `OfflineHelper`: this is a
 *   genuinely split module, not an accidental one.
 */

/**
 * Channel/aggregator -> logo asset name.
 *
 * Both apps' copies are `.js` here and differ only in brace style around the single-statement
 * `if`s — 314 vs 323 tokens, every difference a `{` or `}`. Web's body with annotations added.
 *
 * ⚠️ Resolution order is load-bearing: a known `source` wins outright, but the three hardcoded
 * channel returns below it short-circuit BEFORE the fuzzy `indexOf` match, and the final line
 * falls back through `channelKey` then `source` then a generic webhook logo. Do not flatten
 * this into a single lookup table.
 */
export function getAggregatorLogo(params: any) {
  let channelName = params.channelName;
  let source = params.source;
  const imageMap: any = {
    rista: "rista-logo",
    zomato: "zomato-logo",
    urbanpiper: "urbanpiper-logo",
    zomatotrace: "zomato-logo",
    // limetray: 'limetray-logo',
    foodpanda: "foodpanda-logo",
    olafoods: "olafoods-logo",
    olastores: "olastore-logo",
    swiggy: "swiggy-logo",
    ubereats: "ubereats-logo",
    webhook: "webhook-logo",
    shadowfax: "shadowfax-logo",
    dunzologistics: "dunzo-logo",
    delhivery: "delhivery-logo",
    snaplion: "snapline-logo",
    // amazonfood: 'amazonfood-logo',
    dotpe: "dotpe-logo",
    magicpin: "magicpin-logo",
  };
  if (source && imageMap[source]) return imageMap[source];
  var channelKey: any;
  if (channelName === "Delivery") return "delivery-logo";
  if (channelName === "Dine In") return "dinein-logo";
  if (channelName === "Pickup" || channelName === "Takeaway") return "pickup-logo";
  if (channelName) {
    channelKey = Object.keys(imageMap).find(function (key) {
      return channelName.toLowerCase().indexOf(key) > -1;
    });
  }

  return imageMap[channelKey] || imageMap[source] || "webhook-logo";
}

/**
 * Reads an item's tag ids and reports the flags the item cards render from.
 *
 * **Logic-identical in both apps** (web `:185`, mobile `:98`) — the only differences are quote
 * style and brace-vs-no-brace `if` bodies. The tag NAMES are compared with their exact casing,
 * as both apps do; they are deliberately NOT swapped for `utils/constants`'s `VEGETARIAN` /
 * `NON_VEGETARIAN` / `EGG`, which are lower-cased and are matched against a `.toLowerCase()`d
 * name by `setPriorityForItemTags` above. Substituting them here would silently stop matching.
 *
 * ⚠️ `isFav` is returned but never assigned — dead in BOTH apps. Preserved rather than dropped:
 * consumers destructure it, and removing it would be a behaviour change smuggled into an uplift.
 */
export function checkTag(tagsArr: any[] = []) {
  let isSpicy = false, isChefSpecial = false, itemType = "";
  // Split out of the `let` above only to satisfy prefer-const — it is never assigned (see the
  // docstring). Value and returned shape are unchanged.
  const isFav = false;
  // `: any` is required, not cosmetic — the two programs infer this differently. Under
  // tsconfig.native the mobile store types `catalogStore.itemTags` as `never`, so every
  // `tagsData[tagId].name` read below is a TS2339. The apps' own copies are untyped `.js`.
  const tagsData: any = store.getState().catalogStore.itemTags;
  if (tagsArr.length) {
    tagsArr.forEach((tagId: any) => {
      if (tagsData?.[tagId]) {
        if (tagsData[tagId].name === "Vegetarian")
          itemType = "veg";
        if (tagsData[tagId].name === "Non-vegetarian")
          itemType = "nveg";
        if (tagsData[tagId].name === "Egg")
          itemType = "egg";
        if (tagsData[tagId].name === "Chef Recommended")
          isChefSpecial = true;
        if (tagsData[tagId].name === "Spicy")
          isSpicy = true;
      }
    })
  }
  return ({ isSpicy, isChefSpecial, isFav, itemType });
}

/**
 * Builds the CDN URL for an item's image: the business's static folder + the image id + a size.
 * Returns `""` when either the static folder or the image id is missing.
 *
 * 🔴 **The BODY is identical in both apps; only the base URL's SOURCE diverged** — web read
 * `process.env.REACT_APP_RISTA_ITEM_BASE_URL` (webpack inlines it at build time, so shared code
 * can never read it) and mobile read `mainUrl.REACT_APP_RISTA_ITEM_BASE_URL` from its 114-line
 * react-native-config module. That is exactly the split the effects bridge exists for, so this
 * lives in `shared.ts` and takes the value from `getEnv("ristaItemBaseUrl")`, which both apps
 * wire in `bootstrapCoreBridges` — the same treatment `utils/apiService` gives `ristaApiUrl`
 * and `helpers/feedbackHelper` gives `dotpeApiUrl`.
 *
 * ⚠️ `getEnv` is read INSIDE the function, never at module load — the bridge is wired after core
 * modules evaluate (ledger hazard 18). This function only ever runs at render time.
 */
export function getImageUrl(imageId: string = "", type: string = "small") {
  const baseURL = getEnv("ristaItemBaseUrl"),
    bussinessData: any = store.getState()?.businessStore?.businessData ? store.getState().businessStore.businessData : {};
  let result = "";
  if (bussinessData.staticFolder && imageId) {
    result = `${baseURL}/${bussinessData.staticFolder}images/${imageId}/${type}`;
  }
  return result;
}
